import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './DashboardAssistente.css';

interface ProjetoResumo {
  id: string;
  nome: string;
  titular: { id: string; nome: string } | string;
  progressoGeral?: number;
  progresso?: number;
  dataPrevistaConclusao: string;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

type FilterStatus = 'todos' | 'em-andamento' | 'concluido' | 'nao-iniciado';

function getStatus(progresso: number): FilterStatus {
  if (progresso === 0) return 'nao-iniciado';
  if (progresso === 100) return 'concluido';
  return 'em-andamento';
}

function DashboardAssistente() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  // Apenas Master (e Arquiteto) cadastram empreendimentos; Assistente só faz apontamentos
  const podeCriarProjeto = user?.tipo === 'Master';

  // Filtro por tipo vindo da URL (?tipo=obra|projeto). Sem param = todos.
  const tipoParam = searchParams.get('tipo');
  const tipoFiltro: 'obra' | 'projeto' | null =
    tipoParam === 'obra' || tipoParam === 'projeto' ? tipoParam : null;

  // Rótulos contextuais conforme o tipo
  const tituloTela =
    tipoFiltro === 'obra' ? 'Obras' : tipoFiltro === 'projeto' ? 'Projetos' : 'Empreendimentos';
  const labelNovo =
    tipoFiltro === 'projeto' ? 'Novo Projeto' : tipoFiltro === 'obra' ? 'Nova Obra' : 'Novo';

  const [projetos, setProjetos] = useState<ProjetoResumo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('todos');

  useEffect(() => {
    async function fetchProjetos() {
      try {
        setLoading(true);
        const url = tipoFiltro ? `/empreendimentos?tipo=${tipoFiltro}` : '/empreendimentos';
        const data = await api.get(url);
        setProjetos(data);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar empreendimentos');
      } finally {
        setLoading(false);
      }
    }
    fetchProjetos();
  }, [tipoFiltro]);

  // Rota de criação leva o tipo pré-selecionado para o wizard
  function handleNovo() {
    const tipoNovo = tipoFiltro ?? 'obra';
    navigate(`/admin/empreendimentos/novo?tipo=${tipoNovo}`);
  }

  const filteredProjetos = useMemo(() => {
    return projetos.filter((projeto) => {
      const matchesSearch =
        searchTerm === '' ||
        projeto.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (typeof projeto.titular === 'string' 
          ? projeto.titular.toLowerCase().includes(searchTerm.toLowerCase())
          : projeto.titular.nome.toLowerCase().includes(searchTerm.toLowerCase()));

      const progresso = projeto.progressoGeral ?? projeto.progresso ?? 0;
      const matchesFilter =
        statusFilter === 'todos' || getStatus(progresso) === statusFilter;

      return matchesSearch && matchesFilter;
    });
  }, [projetos, searchTerm, statusFilter]);

  function handleRowClick(projetoId: string) {
    navigate(`/empreendimentos/${projetoId}`);
  }

  if (loading) {
    return (
      <div className="dashboard-assistente">
        <main className="dashboard-assistente__content">
          <p style={{ color: '#c9a96e', textAlign: 'center', marginTop: '2rem' }}>Carregando...</p>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-assistente">
        <main className="dashboard-assistente__content">
          <p style={{ color: '#ff6b6b', textAlign: 'center', marginTop: '2rem' }}>{error}</p>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard-assistente">
      <main className="dashboard-assistente__content">
        <div className="dashboard-assistente__header">
          <div className="dashboard-assistente__title-row">
            <h1 className="dashboard-assistente__title">{tituloTela}</h1>
            {podeCriarProjeto && (
              <button
                className="dashboard-assistente__new-project-btn"
                onClick={handleNovo}
              >
                ➕ {labelNovo}
              </button>
            )}
          </div>

          <div className="dashboard-assistente__search">
            <input
              type="text"
              className="dashboard-assistente__search-input"
              placeholder="Buscar por nome ou titular..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Buscar projetos"
            />
            <select
              className="dashboard-assistente__filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as FilterStatus)}
              aria-label="Filtrar por status"
            >
              <option value="todos">Todos</option>
              <option value="em-andamento">Em Andamento</option>
              <option value="concluido">Concluído</option>
              <option value="nao-iniciado">Não Iniciado</option>
            </select>
          </div>
        </div>

        <div className="dashboard-assistente__table-container">
          {filteredProjetos.length > 0 ? (
            <table className="dashboard-assistente__table">
              <thead>
                <tr>
                  <th>{tipoFiltro === 'projeto' ? 'Nome do Projeto' : tipoFiltro === 'obra' ? 'Nome da Obra' : 'Nome'}</th>
                  <th>Titular</th>
                  <th>Progresso</th>
                  <th>Conclusão Prevista</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjetos.map((projeto) => (
                  <tr
                    key={projeto.id}
                    className="dashboard-assistente__row--clickable"
                    onClick={() => handleRowClick(projeto.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRowClick(projeto.id);
                      }
                    }}
                    tabIndex={0}
                    role="link"
                    aria-label={`Ver projeto ${projeto.nome}`}
                  >
                    <td>{projeto.nome}</td>
                    <td>{typeof projeto.titular === 'string' ? projeto.titular : projeto.titular.nome}</td>
                    <td>
                      <div className="dashboard-assistente__progress">
                        <div className="dashboard-assistente__progress-bar">
                          <div
                            className="dashboard-assistente__progress-fill"
                            style={{ width: `${projeto.progressoGeral ?? projeto.progresso ?? 0}%` }}
                          />
                        </div>
                        <span className="dashboard-assistente__progress-text">
                          {projeto.progressoGeral ?? projeto.progresso ?? 0}%
                        </span>
                      </div>
                    </td>
                    <td>{formatDate(projeto.dataPrevistaConclusao)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="dashboard-assistente__empty">
              {tipoFiltro === 'projeto'
                ? 'Nenhum projeto encontrado com os filtros aplicados.'
                : tipoFiltro === 'obra'
                  ? 'Nenhuma obra encontrada com os filtros aplicados.'
                  : 'Nenhum empreendimento encontrado com os filtros aplicados.'}
            </p>
          )}
        </div>
      </main>
    </div>
  );
}

export default DashboardAssistente;
